import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useAuth0 } from '@auth0/auth0-react';
import { useEffect, useState } from 'react';
import { Spinner } from '@/components/atoms/Spinner';
import { Button } from '@/components/atoms/Button';

export const Route = createFileRoute('/auth/callback')({ component: AuthCallback });

/**
 * Auth0 redirect destination. @auth0/auth0-react handles the code-for-token
 * exchange via its Provider; this page either:
 *   - waits, then navigates to / on success
 *   - surfaces the Auth0 error in the UI on failure (so misconfigured
 *     audience / callback URL / etc. is visible instead of bouncing
 *     silently back to /login)
 *
 * Auth0 reports failures either as an `error` from the SDK after the
 * provider parses the URL, or directly as `?error=...&error_description=...`
 * query params before the SDK runs. We capture both.
 */
function AuthCallback() {
  const { isAuthenticated, isLoading, error, loginWithRedirect } = useAuth0();
  const navigate = useNavigate();
  const [urlError, setUrlError] = useState<{ code: string; description: string } | null>(null);

  // Capture ?error / ?error_description from the URL before they're consumed.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const code = params.get('error');
    if (code) {
      setUrlError({
        code,
        description: params.get('error_description') ?? '(no description provided)',
      });
    }
  }, []);

  useEffect(() => {
    if (isLoading) return;
    if (urlError || error) return; // stop on failure; surface message below
    if (isAuthenticated) navigate({ to: '/', replace: true });
  }, [isAuthenticated, isLoading, error, urlError, navigate]);

  const displayError = urlError ?? (error ? { code: error.message, description: '' } : null);

  if (displayError) {
    return (
      <div className="mx-auto max-w-xl p-8">
        <h1 className="font-display text-title text-foreground">Sign-in failed</h1>
        <p className="mt-2 text-body-sm text-muted-foreground">
          Auth0 rejected the sign-in. Details from the provider:
        </p>
        <div className="mt-4 rounded-md border border-border bg-card p-4">
          <p className="font-mono text-caption">
            <strong>error:</strong> {displayError.code}
          </p>
          {displayError.description ? (
            <p className="mt-2 font-mono text-caption break-words">
              <strong>description:</strong> {displayError.description}
            </p>
          ) : null}
        </div>
        <div className="mt-6 flex gap-3">
          <Button variant="primary" onClick={() => loginWithRedirect()}>
            Try again
          </Button>
          <Button variant="tertiary" onClick={() => navigate({ to: '/login' })}>
            Back to login
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen flex-col items-center justify-center gap-3">
      <Spinner aria-label="Completing sign-in" />
      <p className="text-caption text-muted-foreground">Completing sign-in…</p>
    </div>
  );
}
